import PropTypes from "prop-types";
import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";

import styled from "styled-components/macro";

import UserGivingTransactionsList from "CrossFeatureComponents/UserGivingTransactionsList/UserGivingTransactionsList";
import { useManageMyGivingPage } from "Giving/ManageMyGivingPage/ManageMyGivingPageContext";
import classNames from "classnames";
import { motion } from "framer-motion";

import FnrHoverButton from "UI/FnrHoverButton";
import FnrIcon, { FnrIconWrapper } from "UI/FnrIcon";

import { useLocalStorage } from "Utilities/Hooks/useLocalStorage";
import moveProfileTabToFront from "Utilities/moveProfileTabToFront";

import { StatText } from "../GivingSummary/StatBox";

const MAX_GIFTS_TO_SHOW = 10;

RecentGiftsScreen.propTypes = {
  setLoaded: PropTypes.func,
  isMobile: PropTypes.bool,
};

export default function RecentGiftsScreen({ setLoaded, isMobile }) {
  const [profileTabs, setProfileTabs] = useLocalStorage("userprofile_tab");
  const {
    state: { userGivingData, userID },
  } = useManageMyGivingPage();

  const { transactions } = userGivingData;

  const getTransactions = () => {
    if (!transactions) return [];
    if (transactions.length < 11) return transactions;
    return transactions.slice(0, MAX_GIFTS_TO_SHOW);
  };

  const handleShowMoreClick = () => {
    const reorderedTabs = moveProfileTabToFront(profileTabs, "giving");
    setProfileTabs(reorderedTabs);
    window.location.href = `/dashboard/user/${userID}`;
  };

  useEffect(() => {
    if (transactions?.length > 0 && setLoaded) setLoaded(true);
  }, [transactions?.length, setLoaded]);

  if (!transactions?.length) return null;

  return (
    <ErrorBoundary fallbackRender={() => null}>
      <Wrapper
        inital={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={classNames({ mobile: isMobile })}
      >
        {isMobile ? (
          <TitleBar>
            <TitleText>
              <FnrIcon fad fa-th-list />
              Recent Transactions
            </TitleText>
          </TitleBar>
        ) : (
          <TitleWrapper>
            <StatText>Recent Transactions</StatText>
          </TitleWrapper>
        )}
        <UserGivingTransactionsList transactions={getTransactions()} />
        {transactions?.length > 5 && (
          <ShowMoreButton>
            <FnrHoverButton
              onClick={() => handleShowMoreClick()}
              color="blue"
              size="medium"
            >
              Show More
            </FnrHoverButton>
          </ShowMoreButton>
        )}
      </Wrapper>
    </ErrorBoundary>
  );
}

const Wrapper = styled(motion.div)`
  color: var(--textPrimary);
  &.mobile {
    color: #fff;
    border-radius: 24px;
    background: linear-gradient(
      140deg,
      var(--bgGradient1) 0%,
      var(--bgGradient2) 100%
    );
    display: none;
    grid-area: recents;
    @media (max-width: 1000px) {
      display: block;
    }
  }
`;
const TitleBar = styled.div`
  padding-left: 20px;
  padding-top: 14px;
  display: flex;
`;
const TitleText = styled.div`
  font-size: 18px;
  gap: 6px;
  display: flex;
  align-items: center;
  position: relative;
  padding: 0 1px;
  & ${FnrIconWrapper} {
    font-size: 20px;
  }
  &::after {
    content: "";
    display: block;
    background: var(--textPrimary);
    height: 4px;
    left: 0;
    right: 0;
    position: absolute;
    top: 100%;
    margin-top: 3px;
    border-radius: 5px;
    .mobile & {
      background: #fff;
    }
  }
`;
const ShowMoreButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
`;
const TitleWrapper = styled.div`
  display: flex;
  padding-left: 20px;
`;
